import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Eye } from 'lucide-react';
interface CaseStudy {
  creator: string;
  brand: string;
  brandLogo?: string;
  highlight: string;
  description: string;
  metric: string;
  metricLabel: string;
  icon: React.ElementType;
  youtubeId?: string;
  color: string;
  shadow: string;
}
const caseStudies: CaseStudy[] = [{
  creator: 'Matt Loui',
  brand: 'Skillshare',
  brandLogo: 'https://cdn.magicpatterns.com/uploads/8vzpwh9URL1VmxTV2htyev/skillshare_logo_icon_249611.png',
  highlight: 'Long-Term Partnership',
  description: 'A collaboration that delivered exceptional ROI/ROAS, converting so effectively that it evolved into an ongoing long-term partnership.',
  metric: 'High ROI',
  metricLabel: 'Conversion Rate',
  icon: TrendingUp,
  youtubeId: 'DDLlvJFTNw4',
  color: 'from-green-400 to-emerald-400',
  shadow: 'rgba(110,231,183,0.4)'
}, {
  creator: 'ZAZZA EL Italian',
  brand: 'CyberGhost VPN',
  brandLogo: 'https://cdn.magicpatterns.com/uploads/syzFbVJY22Utjn1RPAgZRC/pngegg_(3).png',
  highlight: 'Viral Success',
  description: 'Strategic content collaboration that resonated with audiences, generating millions of organic views and massive brand awareness.',
  metric: 'Millions',
  metricLabel: 'Views Generated',
  icon: Eye,
  youtubeId: 'cy_qAE4v-Nw',
  color: 'from-blue-400 to-cyan-400',
  shadow: 'rgba(96,165,250,0.4)'
}];
export function CaseStudiesSection() {
  const bounceTransition = {
    type: 'spring',
    stiffness: 260,
    damping: 20
  };
  return <section className="relative z-10 py-16 md:py-32 px-4 md:px-8 max-w-7xl mx-auto" data-id="element-49">
      {/* Section Header */}
      <div className="text-center mb-16 md:mb-24" data-id="element-50">
        <motion.h2 initial={{
        y: 30,
        opacity: 0
      }} whileInView={{
        y: 0,
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        ...bounceTransition,
        delay: 0.1
      }} className="text-5xl md:text-7xl lg:text-8xl font-fredoka font-bold text-gray-800 mb-6" data-id="element-51">
          Proven{' '}
          <motion.span className="inline-block px-6 py-3 bg-gradient-to-r from-green-400 to-emerald-400 text-white rounded-3xl border-4 border-gray-800 shadow-[6px_6px_0px_rgba(45,55,72,1)]" animate={{
          rotate: [-2, 2, -2],
          y: [0, -8, 0]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-52">
            Results
          </motion.span>
        </motion.h2>
        <motion.p initial={{
        y: 20,
        opacity: 0
      }} whileInView={{
        y: 0,
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        ...bounceTransition,
        delay: 0.2
      }} className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto font-medium px-4" data-id="element-53">
          Real collaborations. Real impact. Here's how we've helped creators and
          brands achieve extraordinary outcomes.
        </motion.p>
      </div>

      {/* Case Studies Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10" data-id="element-54">
        {caseStudies.map((study, index) => <motion.div key={study.creator} initial={{
        y: 50,
        opacity: 0,
        rotate: index % 2 === 0 ? -3 : 3
      }} whileInView={{
        y: 0,
        opacity: 1,
        rotate: 0
      }} viewport={{
        once: true
      }} transition={{
        ...bounceTransition,
        delay: index * 0.1
      }} className="group relative" data-id="element-55">
            <div className="relative p-6 md:p-8 rounded-3xl bg-white border-4 border-gray-800 transition-all duration-300 hover:translate-x-[-4px] hover:translate-y-[-4px]" style={{
          boxShadow: `12px 12px 0px ${study.shadow}`
        }} onMouseEnter={e => {
          e.currentTarget.style.boxShadow = `16px 16px 0px ${study.shadow}`;
        }} onMouseLeave={e => {
          e.currentTarget.style.boxShadow = `12px 12px 0px ${study.shadow}`;
        }} data-id="element-56">
              {/* Icon Badge */}
              <div className={`mb-6 inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br ${study.color} border-3 border-gray-800 text-white group-hover:rotate-12 group-hover:scale-110 transition-all`} data-id="element-57">
                <study.icon className="w-6 h-6" data-id="element-58" />
              </div>

              {/* Highlight Label */}
              <span className="inline-block px-4 py-2 rounded-full bg-yellow-200 border-2 border-gray-800 text-gray-800 text-xs font-bold tracking-wide uppercase mb-4" data-id="element-59">
                {study.highlight}
              </span>

              {/* Creator × Brand */}
              <div className="flex items-center gap-3 mb-6 flex-wrap" data-id="element-60">
                <h3 className="text-2xl md:text-3xl font-fredoka font-bold text-gray-800" data-id="element-61">
                  {study.creator}
                </h3>
                <span className="text-2xl md:text-3xl text-gray-400" data-id="element-62">×</span>
                {study.brandLogo ? <img src={study.brandLogo} alt={study.brand} className="h-8 md:h-10 w-auto" loading="lazy" data-id="element-63" /> : <span className="text-2xl md:text-3xl font-fredoka font-bold text-gray-600" data-id="element-64">
                    {study.brand}
                  </span>}
              </div>

              {/* YouTube Video */}
              {study.youtubeId && <div className="mb-6 rounded-2xl overflow-hidden aspect-video border-4 border-gray-800 shadow-[4px_4px_0px_rgba(45,55,72,0.3)]" data-id="element-65">
                  <iframe src={`https://www.youtube.com/embed/${study.youtubeId}`} title={`${study.creator} × ${study.brand}`} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full h-full" loading="lazy" data-id="element-66" />
                </div>}

              <p className="text-gray-600 leading-relaxed mb-6 font-medium" data-id="element-67">
                {study.description}
              </p>

              {/* Metric */}
              <div className="pt-6 border-t-4 border-gray-800 flex items-baseline gap-3" data-id="element-68">
                <span className="text-4xl md:text-5xl font-fredoka font-bold text-gray-800" data-id="element-69">
                  {study.metric}
                </span>
                <span className="text-gray-600 text-sm font-bold uppercase tracking-wide" data-id="element-70">
                  {study.metricLabel}
                </span>
              </div>
            </div>
          </motion.div>)}
      </div>
    </section>;
}